import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyProfile, getMyAppointments } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CalendarDays, User, ChevronRight } from 'lucide-react';
import { getApiErrorMessage } from '../../utils/apiError';
import { toast } from 'sonner';

const statusLabels = {
  PENDIENTE: 'Pendiente',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  NO_SHOW: 'No asistió',
  COMPLETADA: 'Completada',
  REQUIERE_REAGENDAR: 'Por reagendar',
};

export default function PatientDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, appointmentsRes] = await Promise.all([
          getMyProfile(),
          getMyAppointments({ start_date: new Date().toISOString().split('T')[0] }),
        ]);
        setProfile(profileRes.data?.data || profileRes.data);
        const list = appointmentsRes.data?.data ?? appointmentsRes.data ?? [];
        const arr = Array.isArray(list) ? list : [];
        setUpcoming(arr.filter((a) => a.status !== 'CANCELADA').slice(0, 3));
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Error al cargar'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const nextAppointment = upcoming[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-10 h-10 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2B2D42] font-['Manrope']">
          Hola, {profile?.first_name || user?.name?.split(' ')[0] || 'Cliente'}
        </h1>
        <p className="text-[#5D5C61] mt-1">Aquí puedes ver tus citas y tu perfil.</p>
      </div>

      {nextAppointment && (
        <Card className="border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#335C67]" />
              Próxima cita
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium text-[#2B2D42]">{formatDate(nextAppointment.date)}</p>
            <p className="text-[#5D5C61]">
              {nextAppointment.start_time} - {nextAppointment.end_time}
            </p>
            {nextAppointment.reason && (
              <p className="text-sm text-[#8D99AE] mt-1">{nextAppointment.reason}</p>
            )}
            <p className="text-sm mt-2">
              <span className="text-[#8D99AE]">Estado: </span>
              <span className="font-medium">{statusLabels[nextAppointment.status] || nextAppointment.status}</span>
            </p>
            <Button asChild className="mt-4 bg-[#335C67] hover:bg-[#244048] rounded-full" size="sm">
              <Link to={`/mis-citas/${nextAppointment.id}`}>
                Ver detalle <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {!nextAppointment && (
        <Card className="border-slate-100">
          <CardContent className="py-8 text-center">
            <CalendarDays className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
            <p className="text-[#5D5C61]">No tienes citas próximas</p>
            <Button asChild className="mt-4 rounded-full" variant="outline">
              <Link to="/mis-citas">Ver mis citas</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Button asChild variant="outline" className="h-auto py-4 justify-start rounded-xl" size="lg">
          <Link to="/mis-citas" className="flex items-center gap-3 w-full">
            <CalendarDays className="w-5 h-5 text-[#335C67]" />
            <span>Ver todas mis citas</span>
            <ChevronRight className="w-4 h-4 ml-auto text-[#8D99AE]" />
          </Link>
        </Button>
        <Button asChild variant="outline" className="h-auto py-4 justify-start rounded-xl" size="lg">
          <Link to="/mi-cuenta" className="flex items-center gap-3 w-full">
            <User className="w-5 h-5 text-[#335C67]" />
            <span>Mi cuenta</span>
            <ChevronRight className="w-4 h-4 ml-auto text-[#8D99AE]" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
